'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Card, CardBody, Avatar } from '@heroui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useScheduler } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import {
  OptimalSlotType,
  SlotOptPopulated,
} from '@/util/types/optimalSlotType';
import { Service, Bundle, Inspector } from '@/util/types/serverTypes';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import CalendarView from './CalendarView';
import InspectorFilter from './InspectorFilter';
import {
  FaArrowRight,
  FaRotateLeft,
  FaStar,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa6';
import { FaCheckCircle, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { useTZDayjs } from '@/context/TimezoneContext';
import { Dayjs } from 'dayjs';
// import { SLOTS_TO_FETCH } from '../constants';
import { useStartOver } from '../hooks/useStartOver';
import { useCountdown } from '../hooks/useCountdown';
import SendQuoteLoader from '../summary/SendQuoteLoader';
import StandardModal from '@/components/ui/StandardModal';
import posthog from 'posthog-js';
import { EVENTS } from '@attik/posthog';
import { jobDurationHoursForScheduling } from '@/util/functions/schedulingHelpers/serviceDurationHelpers';

type SlotsStepProps = {
  allServices: Service[];
  allBundles: Bundle[];
};

export default function SlotsStep({ allServices, allBundles }: SlotsStepProps) {
  const {
    selectedPrimaryService,
    address,
    selectedDateTime,
    setSelectedDateTime,
    selectedInspectorForSlot,
    setSelectedInspectorForSlot,
    setSelectedSlotInspectorInfo,
    selectedSlotInspectorInfo,
    priceData,
    isLoadingDraft,
    scheduleSettings,
    setSelectedRole,
    workingHoldExpiresAt,
    jwtToken,
    companyId,
    companyStyles,
    selectedInspectorIdsForFilter,
    setSelectedInspectorIdsForFilter,
    isSendingQuote,
    setIsSendingQuote,
    cancelDebounce,
    draftQuoteId,
    draftQuoteSlug,
  } = useScheduler();

  const { nextStep } = useStepNavigation();
  const tzDayjs = useTZDayjs();

  // Countdown timer for working hold
  const countdown = useCountdown(workingHoldExpiresAt);

  // Initialize to Monday of current week
  const [startDate, setStartDate] = useState<Dayjs>(
    tzDayjs().startOf('week').add(1, 'day')
  );

  const [allFetchedSlots, setAllFetchedSlots] =
    useState<OptimalSlotType | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedDateRange, setCachedDateRange] = useState<{
    start: Dayjs;
    end: Dayjs;
  } | null>(null);
  const [savedTopRecommendations, setSavedTopRecommendations] = useState<
    SlotOptPopulated[]
  >([]);
  const [showMoreRecommendations, setShowMoreRecommendations] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSendQuoteModalOpen, setIsSendQuoteModalOpen] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Tailwind md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Hydrate calendar when datetime is pre-selected (e.g., from draft)
  // Only navigate if the slot is outside the current view
  useEffect(() => {
    if (!isLoadingDraft && selectedDateTime) {
      const selectedDate = tzDayjs(selectedDateTime);
      const endDate = startDate.add(13, 'day').endOf('day');
      const isOutsideView =
        selectedDate.isBefore(startDate, 'day') ||
        selectedDate.isAfter(endDate, 'day');

      if (isOutsideView) {
        const mondayOfSelectedWeek = selectedDate.startOf('week').add(1, 'day');
        setStartDate(mondayOfSelectedWeek);
      }
    }
  }, [selectedDateTime, isLoadingDraft, tzDayjs, startDate]);

  // Get total duration from context (properly calculated with modifiers, equipment, etc.)
  const totalDuration = priceData?.totalDuration ?? 0;
  const slotDurationHours = jobDurationHoursForScheduling(totalDuration);

  // Helper to calculate time range for a slot
  const getTimeRange = (datetime: string | Date) => {
    const startTime = tzDayjs(datetime);
    const endTime = startTime.add(slotDurationHours * 60, 'minutes');
    return {
      start: startTime.format('h:mma'),
      end: endTime.format('h:mma'),
    };
  };

  // Filter slots for the current visible 2-week period and by selected inspectors/teams/services
  const currentOptSlots = useMemo(() => {
    if (!allFetchedSlots?.slotsSorted) return null;

    const endDate = startDate.add(14, 'day').endOf('day');
    const visibleSlots = allFetchedSlots.slotsSorted.filter((slot) => {
      const slotDate = tzDayjs(slot.datetime);
      // Check if slot is within the 2-week window
      const isInDateRange =
        (slotDate.isAfter(startDate) || slotDate.isSame(startDate, 'day')) &&
        (slotDate.isBefore(endDate) || slotDate.isSame(endDate, 'day'));

      // Filter by selected inspectors if any are selected
      if (selectedInspectorIdsForFilter?.length > 0) {
        const matchesInspector = selectedInspectorIdsForFilter.includes(
          slot._inspectorId._id
        );
        if (!matchesInspector) return false;
      }

      return isInDateRange;
    });

    return {
      ...allFetchedSlots,
      slotsSorted: visibleSlots,
    };
  }, [
    allFetchedSlots,
    startDate,
    tzDayjs,
    selectedInspectorIdsForFilter,
  ]);

  // Calculate top 3 recommendations from ALL fetched slots (not just visible ones)
  const calculateTopRecommendations = useMemo(() => {
    if (!allFetchedSlots?.slotsSorted) return [];

    const now = tzDayjs();
    const tomorrow = now.add(1, 'day').startOf('day');
    const cutoffMinutes = scheduleSettings?.nextDayCutoffTimeInMinutes;
    const displayDayOfSlots = scheduleSettings?.displayDayOfSlots;

    // Filter out past slots and group by date
    const slotsByDate = new Map<string, SlotOptPopulated[]>();
    allFetchedSlots.slotsSorted.forEach((slot) => {
      const slotTime = tzDayjs(slot.datetime);

      // Filter by selected inspectors if any are selected
      if (selectedInspectorIdsForFilter?.length > 0) {
        if (!selectedInspectorIdsForFilter.includes(slot._inspectorId._id)) {
          return;
        }
      }

      // Skip slots that are in the past
      if (slotTime.isBefore(now)) {
        return;
      }

      // Once the cutoff time has passed, hide next-day slots, and same-day
      // slots when day-of slots are enabled (mirrors backend suppression).
      if (
        cutoffMinutes !== undefined &&
        cutoffMinutes !== null &&
        cutoffMinutes >= 0
      ) {
        const currentMinutes = now.hour() * 60 + now.minute();
        if (currentMinutes >= cutoffMinutes) {
          const slotDateStr = slotTime.format('YYYY-MM-DD');
          const todayStr = now.format('YYYY-MM-DD');
          const tomorrowStr = tomorrow.format('YYYY-MM-DD');
          if (slotDateStr === tomorrowStr) {
            return; // Past cutoff — hide all next-day slots
          }
          if (displayDayOfSlots && slotDateStr === todayStr) {
            return; // Past cutoff — hide all same-day slots
          }
        }
      }

      const dateKey = slotTime.format('YYYY-MM-DD');
      if (!slotsByDate.has(dateKey)) {
        slotsByDate.set(dateKey, []);
      }
      slotsByDate.get(dateKey)!.push(slot);
    });

    // Get best slot from each of the first 3 days with availability
    const recommendations: SlotOptPopulated[] = [];
    const sortedDates = Array.from(slotsByDate.keys()).sort();

    for (let i = 0; i < Math.min(3, sortedDates.length); i++) {
      const dateSlots = slotsByDate.get(sortedDates[i])!;
      const bestSlot = dateSlots.reduce((best, current) =>
        current.score > best.score ? current : best
      );
      recommendations.push(bestSlot);
    }

    return recommendations;
  }, [
    allFetchedSlots,
    tzDayjs,
    scheduleSettings,
    selectedInspectorIdsForFilter,
  ]);

  // Save initial top recommendations on first load or when filters change
  useEffect(() => {
    if (
      calculateTopRecommendations.length > 0 &&
      savedTopRecommendations.length === 0
    ) {
      setSavedTopRecommendations(calculateTopRecommendations);
    }
  }, [calculateTopRecommendations, savedTopRecommendations.length]);

  // Use saved recommendations for display (fallback to calculated if none saved yet)
  const topRecommendations =
    savedTopRecommendations.length > 0
      ? savedTopRecommendations
      : calculateTopRecommendations;

  // Extract unique inspector IDs from all available slots
  const availableInspectorIds = useMemo(() => {
    if (!allFetchedSlots?.slotsSorted) return new Set<string>();

    const inspectorIdSet = new Set<string>();
    allFetchedSlots.slotsSorted.forEach((slot) => {
      if (slot._inspectorId?._id) {
        inspectorIdSet.add(slot._inspectorId._id);
      }
    });

    return inspectorIdSet;
  }, [allFetchedSlots]);

  // Clear saved recommendations only when service/address change (NOT on pagination or inspector filter)
  useEffect(() => {
    setSavedTopRecommendations([]);
  }, [selectedPrimaryService, address, totalDuration]);

  // Fetch optimal slots with smart caching (only refetch when needed)
  useEffect(() => {
    if (!selectedPrimaryService || !address) {
      setAllFetchedSlots(null);
      setCachedDateRange(null);
      setError(null);
      return;
    }

    const fetchSlots = async () => {
      // Check if we need to refetch based on cached range
      const needsRefetch =
        !cachedDateRange ||
        startDate.isBefore(cachedDateRange.start) ||
        startDate.isAfter(cachedDateRange.end.subtract(14, 'day'));

      if (!needsRefetch) {
        // We have cached data that covers the current view
        return;
      }

      const isInitialLoad = !allFetchedSlots;

      // Show full loading spinner only on initial load
      if (isInitialLoad) {
        setSlotsLoading(true);
      } else {
        setIsBackgroundLoading(true);
      }
      setError(null);

      try {
        const params: Record<string, any> = {
          serviceId: selectedPrimaryService,
          coords: `${address.lng},${address.lat}`,
          // Request slots starting from the current startDate for pagination
          startDate: startDate.format('YYYY-MM-DD'),
          maxSlots: 200, // Balanced for good calendar coverage with optimized performance
          // Apply slot filters for scheduler (exclude problematic slots)
          excludeOverlapping: true,
          excludeOutOfLevel: true,
          excludeOutOfServiceArea: true,
          minDays: isMobile ? 7 : 14,
          // Pass displayDayOfSlots from company settings; omitting it falls back to
          // the brand setting on the backend.
          ...(scheduleSettings?.displayDayOfSlots && { displayDayOfSlots: true }),
          // Client-facing surface: subject to the Online Booking Cutoff Time. Declared
          // explicitly so the restriction is a stated contract rather than a default
          // this caller happens to inherit.
          scheduledLocation: 'online',
        };

        params.duration = slotDurationHours;
        if (address.sqft) params.sqft = address.sqft;
        if (address.yearBuilt) params.propertyYear = address.yearBuilt;

        // Don't filter by inspector on API call - we'll filter client-side
        // This allows us to filter without refetching

        const data = await callServerAction<OptimalSlotType>({
          url: 'schedule/optimal-slots',
          method: 'get',
          params: {
            ...params,
            _companyId: companyId,
          },
          headers: {
            'client-token': jwtToken,
          },
        });

        setAllFetchedSlots(data);

        // Update cached date range
        if (data?.slotsSorted && data.slotsSorted.length > 0) {
          const firstSlot = tzDayjs(data.slotsSorted[0].datetime);
          const lastSlot = tzDayjs(
            data.slotsSorted[data.slotsSorted.length - 1].datetime
          );
          setCachedDateRange({
            start: firstSlot,
            end: lastSlot,
          });
        }
      } catch (error) {
        setError(
          'Failed to load available time slots. Please try again or contact support.'
        );
        if (isInitialLoad) {
          setAllFetchedSlots(null);
        }
      } finally {
        setSlotsLoading(false);
        setIsBackgroundLoading(false);
      }
    };

    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPrimaryService,
    address,
    slotDurationHours,
    startDate,
    // Note: debouncedInspectorIds removed - inspector filtering is now client-side only
  ]);

  const handleTimeSlotSelect = (datetime: string, inspectorId?: string) => {
    setSelectedDateTime(datetime);

    posthog.capture(EVENTS.schedulerSlotSelected, {
      company_id: companyId,
      scheduled_date: datetime,
    });

    if (inspectorId) {
      setSelectedInspectorForSlot({
        ...selectedInspectorForSlot,
        [datetime]: inspectorId,
      });
    }

    // Save the inspector info for this slot so it persists across calendar navigation
    let selectedInspector = null;
    if (allFetchedSlots?.slotsSorted) {
      const targetInspectorId =
        inspectorId || selectedInspectorForSlot[datetime];
      const slot = allFetchedSlots.slotsSorted.find(
        (s) =>
          s.datetime === datetime &&
          (!targetInspectorId || s._inspectorId._id === targetInspectorId)
      );
      if (slot) {
        selectedInspector = slot._inspectorId;
        setSelectedSlotInspectorInfo({
          _id: slot._inspectorId._id,
          firstName: slot._inspectorId.firstName,
          lastName: slot._inspectorId.lastName,
          thumb: slot._inspectorId.thumb,
        });
      }
    }

    // Draft will be auto-saved by DraftAutoSaver on field change

    // Navigate calendar to show the selected slot if it's not in the current view
    const slotDate = tzDayjs(datetime);
    const endDate = startDate.add(13, 'day').endOf('day');

    // Check if slot is outside current 2-week view
    if (
      slotDate.isBefore(startDate, 'day') ||
      slotDate.isAfter(endDate, 'day')
    ) {
      // Navigate to a 2-week period that includes this slot
      // Start the period on Monday of the week containing the slot
      const newStartDate = slotDate.startOf('week').add(1, 'day');
      setStartDate(newStartDate);
    }
  };

  const handleWeekChange = (newStartDate: Dayjs) => {
    setStartDate(newStartDate);
    // Changing startDate will trigger refetch via useEffect
  };

  // Refs so timer callbacks always see the latest values without recreating timers
  const selectedDateTimeRef = useRef(selectedDateTime);
  useEffect(() => {
    selectedDateTimeRef.current = selectedDateTime;
  }, [selectedDateTime]);

  const scheduleSettingsRef = useRef(scheduleSettings);
  useEffect(() => {
    scheduleSettingsRef.current = scheduleSettings;
  }, [scheduleSettings]);

  // Invalidate slot cache at midnight (company timezone) so that a session open
  // overnight does not show same-day slots that were "tomorrow" when fetched.
  useEffect(() => {
    const now = tzDayjs();
    const msUntilMidnight = now.add(1, 'day').startOf('day').diff(now);

    const timer = window.setTimeout(() => {
      setAllFetchedSlots(null);
      setCachedDateRange(null);
      setSavedTopRecommendations([]);

      // If the selected slot just became same-day and same-day booking is disabled, clear it
      const dt = selectedDateTimeRef.current;
      const settings = scheduleSettingsRef.current;
      if (dt && settings && !settings.displayDayOfSlots) {
        const nowAfter = tzDayjs();
        if (tzDayjs(dt).isSame(nowAfter, 'day')) {
          setSelectedDateTime(null);
        }
      }
    }, msUntilMidnight);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount — refs provide fresh values at fire time

  // Invalidate cached slots and saved recommendations when the Online Booking
  // Cutoff Time passes so stale same-day/next-day slots and recommendation
  // cards are cleared for the currently open session.
  useEffect(() => {
    const cutoff = scheduleSettings?.nextDayCutoffTimeInMinutes;
    if (cutoff == null || cutoff < 0) return;

    const now = tzDayjs();
    const cutoffToday = now.startOf('day').add(cutoff, 'minute');
    if (!cutoffToday.isAfter(now)) return; // cutoff already passed today

    const msUntilCutoff = cutoffToday.diff(now);

    const timer = window.setTimeout(() => {
      setSavedTopRecommendations([]);
      // Clear cached slots so post-cutoff same-day/next-day slots are refetched
      // fresh from the server (which now suppresses them).
      setAllFetchedSlots(null);
      setCachedDateRange(null);

      // Clear the current selection if it just became unbookable: next-day
      // slots are always blocked after cutoff; same-day slots are blocked when
      // day-of slots are enabled.
      const dt = selectedDateTimeRef.current;
      const settings = scheduleSettingsRef.current;
      if (dt) {
        const nowAfter = tzDayjs();
        const selectedDay = tzDayjs(dt).format('YYYY-MM-DD');
        const todayStr = nowAfter.format('YYYY-MM-DD');
        const tomorrowStr = nowAfter.add(1, 'day').format('YYYY-MM-DD');
        if (selectedDay === tomorrowStr) {
          setSelectedDateTime(null);
        } else if (settings?.displayDayOfSlots && selectedDay === todayStr) {
          setSelectedDateTime(null);
        }
      }
    }, msUntilCutoff);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSettings?.nextDayCutoffTimeInMinutes]); // re-arm if the setting changes

  // Validate selectedDateTime restored from a draft against current schedule rules.
  // A draft saved before midnight or before the cutoff may contain a datetime that
  // is no longer bookable.
  useEffect(() => {
    if (isLoadingDraft || !selectedDateTime || !scheduleSettings) return;

    const now = tzDayjs();
    const selectedDay = tzDayjs(selectedDateTime).format('YYYY-MM-DD');
    const todayStr = now.format('YYYY-MM-DD');
    const tomorrowStr = now.add(1, 'day').format('YYYY-MM-DD');

    if (!scheduleSettings.displayDayOfSlots && selectedDay === todayStr) {
      setSelectedDateTime(null);
      return;
    }

    const cutoff = scheduleSettings.nextDayCutoffTimeInMinutes;
    if (cutoff != null && cutoff >= 0) {
      const nowMinutes = now.hour() * 60 + now.minute();
      if (nowMinutes >= cutoff) {
        // After the cutoff, next-day slots are always blocked, and same-day
        // slots are blocked when day-of slots are enabled.
        if (selectedDay === tomorrowStr) {
          setSelectedDateTime(null);
        } else if (scheduleSettings.displayDayOfSlots && selectedDay === todayStr) {
          setSelectedDateTime(null);
        }
      }
    }
    // Only re-run when the draft finishes loading; values are captured fresh at that point
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingDraft]);

  const handleContinue = () => {
    if (selectedDateTime) {
      nextStep();
    }
  };

  const handleClearSelection = () => {
    setSelectedDateTime(null);
    setSelectedInspectorForSlot({});
    setSelectedSlotInspectorInfo(null);
  };

  const handleInspectorFilterChange = (ids: string[]) => {
    setSelectedInspectorIdsForFilter(ids);
    if (selectedDateTime) {
      handleClearSelection();
    }
  };

  const handleStartOver = useStartOver({
    navigateToStep: 1,
    onBeforeReset: () => {
      // Clear step-specific local state
      setStartDate(tzDayjs().startOf('week').add(1, 'day'));
      setAllFetchedSlots(null);
      setCachedDateRange(null);
      setSavedTopRecommendations([]);
      setError(null);
      setSelectedInspectorIdsForFilter([]);
      setIsBackgroundLoading(false);
      setSlotsLoading(false);
      // Clear selectedRole so services step starts at step 0 (Filter Form) instead of step 1
      setSelectedRole(null);
    },
  });

  // Get inspector info for selected slot
  const getSelectedInspector = () => {
    if (!selectedDateTime) return null;

    // First, try to get from allFetchedSlots if available
    if (allFetchedSlots?.slotsSorted) {
      const slot = allFetchedSlots.slotsSorted.find(
        (s) => s.datetime === selectedDateTime
      );
      if (slot) {
        // If specific inspector selected for this slot, use that
        if (selectedInspectorForSlot[selectedDateTime]) {
          const inspectorId = selectedInspectorForSlot[selectedDateTime];
          const matchingSlot = allFetchedSlots.slotsSorted.find(
            (s) =>
              s.datetime === selectedDateTime &&
              s._inspectorId._id === inspectorId
          );
          if (matchingSlot) return matchingSlot._inspectorId;
        }
        return slot._inspectorId;
      }
    }

    // Fallback to saved inspector info if slot not in current fetch
    return selectedSlotInspectorInfo;
  };

  // Handle Send Quote - just set the state, loader component will handle the actual sending
  const handleSendQuote = () => {
    if (!draftQuoteId || !address || !priceData) {
      return; // Validation handled in SendQuoteLoader
    }

    // Cancel any pending debounced saves to prevent race condition
    cancelDebounce();

    // Set state to show loader - SendQuoteLoader will handle the actual sending
    setIsSendingQuote(true);
  };

  // Handle opening the confirmation modal
  const handleOpenSendQuoteModal = () => {
    setIsSendQuoteModalOpen(true);
  };

  // Handle confirming and sending the quote
  const handleConfirmSendQuote = () => {
    setIsSendQuoteModalOpen(false);
    handleSendQuote();
  };

  // Check if prerequisites are met
  const hasPrerequisites = selectedPrimaryService && address;

  // If sending quote is in progress, show the loader
  if (isSendingQuote) {
    return (
      <SendQuoteLoader
        onComplete={() => {
          setIsSendingQuote(false);
        }}
        onError={(error) => {
          setIsSendingQuote(false);
        }}
        onRetry={() => {
          setIsSendingQuote(false);
        }}
      />
    );
  }

  return (
    <div className='mx-auto min-h-[600px] max-w-6xl space-y-6'>
      {/* Header Section */}
      <div className='text-center'>
        <h2 className='mb-2 text-3xl font-bold'>Choose Your Time Slot</h2>
        <p className='text-gray-600'>
          Select a convenient date and time for your inspection
        </p>
      </div>

      {/* Prerequisites Check */}
      {!hasPrerequisites && (
        <Card className='border-2 border-warning bg-warning-50'>
          <CardBody>
            <div className='text-center'>
              <p className='mb-2 font-semibold text-warning-800'>
                Complete Previous Steps
              </p>
              <p className='text-sm text-warning-700'>
                {!selectedPrimaryService &&
                  !address &&
                  'Please select a service and enter a property address to view available time slots.'}
                {!selectedPrimaryService &&
                  address &&
                  'Please select a service to view available time slots.'}
                {selectedPrimaryService &&
                  !address &&
                  'Please enter a property address to view available time slots.'}
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Send Quote Option */}
      {hasPrerequisites && (
        <div className='flex justify-center'>
          <p className='text-sm text-gray-600'>
            Not ready to schedule?{' '}
            <button
              onClick={handleOpenSendQuoteModal}
              disabled={isSendingQuote}
              className='cursor-pointer underline transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50'
              style={{
                color:
                  companyStyles.secondaryButtonStyles?.backgroundColor ||
                  companyStyles.companyColor ||
                  '#006FEE',
              }}
            >
              {isSendingQuote ? 'sending...' : 'Send yourself a quote'}
            </button>
          </p>
        </div>
      )}

      {/* Send Quote Confirmation Modal */}
      <StandardModal
        show={isSendQuoteModalOpen}
        closeHandler={() => setIsSendQuoteModalOpen(false)}
        title='Send Quote'
        acceptHandler={handleConfirmSendQuote}
        acceptName='Send Quote'
        closeName='Cancel'
        size='md'
      >
        <div className='space-y-4'>
          <p className='text-gray-700'>
            We'll send you an email with a link to your quote. You can
            continue scheduling and complete your quote at any time.
          </p>
          <div className='rounded-lg bg-blue-50 p-4'>
            <p className='text-sm text-gray-600'>
              <strong>What happens next:</strong>
            </p>
            <ul className='mt-2 ml-4 list-disc space-y-1 text-sm text-gray-600'>
              <li>You'll receive an email with a link to your quote</li>
              <li>You can continue scheduling when you're ready</li>
              <li>Your quote details will be saved</li>
            </ul>
          </div>
        </div>
      </StandardModal>

      {/* Top Recommendation Banner */}
      <AnimatePresence mode='wait'>
        {hasPrerequisites &&
          !error &&
          topRecommendations[0] &&
          !selectedDateTime && (
            <motion.div
              key='recommendations-banner'
              initial={{
                opacity: 0,
                height: 0,
                y: -10,
                scale: 0.95,
              }}
              animate={{
                opacity: 1,
                height: 'auto',
                y: 0,
                scale: 1,
                transition: {
                  opacity: { duration: 0.2, ease: 'easeOut' },
                  height: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
                  y: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
                  scale: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
                },
              }}
              exit={{
                opacity: 0,
                height: 0,
                y: -10,
                scale: 0.95,
                transition: {
                  opacity: { duration: 0.15, ease: 'easeIn' },
                  height: { duration: 0.3, ease: [0.4, 0, 1, 1] },
                  y: { duration: 0.3, ease: [0.4, 0, 1, 1] },
                  scale: { duration: 0.3, ease: [0.4, 0, 1, 1] },
                },
              }}
              style={{ overflow: 'hidden' }}
            >
              <Card className='w-full border-2 border-warning bg-warning-50'>
                <CardBody>
                  <div className='space-y-4'>
                    {/* #1 Recommendation - Featured Full Width */}
                    <button
                      onClick={() =>
                        handleTimeSlotSelect(
                          topRecommendations[0].datetime,
                          topRecommendations[0]._inspectorId._id
                        )
                      }
                      className='flex w-full cursor-pointer items-center justify-between gap-4 rounded-lg border-2 border-warning-400 bg-white p-4 shadow-md transition-all hover:border-warning-500 hover:shadow-lg'
                    >
                      <div className='flex items-center gap-3'>
                        <div className='flex items-center justify-center rounded-full bg-warning px-3 py-1.5'>
                          <FaStar className='mr-1 text-warning-900' size={14} />
                          <span className='text-sm font-bold text-warning-900'>
                            #1
                          </span>
                        </div>
                        {scheduleSettings.showInspectorInOnlineScheduler && (
                          <Avatar
                            src={topRecommendations[0]._inspectorId.thumb}
                            alt={`${topRecommendations[0]._inspectorId.firstName} ${topRecommendations[0]._inspectorId.lastName}`}
                            size='md'
                            className='shrink-0'
                          />
                        )}
                        <div className='text-left'>
                          <p className='text-xs font-semibold text-warning-900 sm:text-sm'>
                            Soonest available
                          </p>
                          <p className='text-base font-bold text-warning-900 sm:text-lg'>
                            {tzDayjs(topRecommendations[0].datetime).format(
                              'dddd, MMMM D'
                            )}
                          </p>
                          <p className='text-sm text-gray-700'>
                            {(() => {
                              const timeRange = getTimeRange(
                                topRecommendations[0].datetime
                              );
                              return timeRange
                                ? `${timeRange.start}-${timeRange.end}`
                                : tzDayjs(
                                  topRecommendations[0].datetime
                                ).format('h:mma');
                            })()}
                            {scheduleSettings.showInspectorInOnlineScheduler && (
                              <>
                                {' • with '}
                                {
                                  topRecommendations[0]._inspectorId.firstName
                                }{' '}
                                {topRecommendations[0]._inspectorId.lastName}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <FaArrowRight
                        className='shrink-0 text-warning-600'
                        size={20}
                      />
                    </button>

                    {/* More Recommendations Button */}
                    {topRecommendations.length > 1 && (
                      <button
                        onClick={() =>
                          setShowMoreRecommendations(!showMoreRecommendations)
                        }
                        className='flex w-full items-center justify-center gap-2 text-sm text-warning-700 transition-colors hover:text-warning-900'
                      >
                        <span>
                          {showMoreRecommendations
                            ? 'Show fewer'
                            : `Show ${topRecommendations.length - 1
                            } more recommendation${topRecommendations.length - 1 !== 1 ? 's' : ''
                            }`}
                        </span>
                        {showMoreRecommendations ? (
                          <FaChevronUp size={12} />
                        ) : (
                          <FaChevronDown size={12} />
                        )}
                      </button>
                    )}

                    {/* #2 and #3 Recommendations - Stacked Vertically */}
                    <AnimatePresence>
                      {topRecommendations.length > 1 &&
                        showMoreRecommendations && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{
                              opacity: { duration: 0.2 },
                              height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                            }}
                            style={{ overflow: 'hidden' }}
                            className='space-y-3'
                          >
                            {topRecommendations
                              .slice(1, 3)
                              .map((slot, index) => (
                                <button
                                  key={slot.datetime}
                                  onClick={() =>
                                    handleTimeSlotSelect(
                                      slot.datetime,
                                      slot._inspectorId._id
                                    )
                                  }
                                  className='w-full cursor-pointer rounded-lg border border-warning-300 bg-white p-3 text-left transition-all hover:border-warning-400 hover:shadow-md'
                                >
                                  <div className='flex items-center justify-between gap-2'>
                                    <div className='flex min-w-0 flex-1 items-center gap-2'>
                                      <div className='flex items-center justify-center rounded-full bg-warning-100 px-2 py-1'>
                                        <span className='text-xs font-bold text-warning-800'>
                                          #{index + 2}
                                        </span>
                                      </div>
                                      {scheduleSettings.showInspectorInOnlineScheduler && (
                                        <Avatar
                                          src={slot._inspectorId.thumb}
                                          alt={`${slot._inspectorId.firstName} ${slot._inspectorId.lastName}`}
                                          size='sm'
                                          className='shrink-0'
                                        />
                                      )}
                                      <div className='min-w-0 flex-1 text-left'>
                                        <p
                                          className={`${scheduleSettings.showInspectorInOnlineScheduler
                                              ? 'text-xs'
                                              : 'text-base'
                                            } truncate font-medium text-gray-900`}
                                        >
                                          {tzDayjs(slot.datetime).format(
                                            'ddd, MMM D'
                                          )}
                                        </p>
                                        <p className='truncate text-xs text-gray-600'>
                                          {(() => {
                                            const timeRange = getTimeRange(
                                              slot.datetime
                                            );
                                            return timeRange
                                              ? `${timeRange.start} - ${timeRange.end}`
                                              : tzDayjs(slot.datetime).format(
                                                'h:mma'
                                              );
                                          })()}
                                          {scheduleSettings.showInspectorInOnlineScheduler && (
                                            <>
                                              {' • '}
                                              {slot._inspectorId.firstName}{' '}
                                              {slot._inspectorId.lastName}
                                            </>
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                    <FaArrowRight
                                      className='shrink-0 text-warning-600'
                                      size={18}
                                    />
                                  </div>
                                </button>
                              ))}
                          </motion.div>
                        )}
                    </AnimatePresence>
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          )}
      </AnimatePresence>

      {/* Error Message */}
      {error && (
        <Card className='border-2 border-danger bg-danger-50'>
          <CardBody>
            <div className='flex items-center gap-3'>
              <FaExclamationTriangle className='text-danger' size={24} />
              <div>
                <p className='font-semibold text-danger-800'>
                  Error Loading Slots
                </p>
                <p className='text-sm text-danger-700'>{error}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Selected Time Confirmation */}
      <AnimatePresence mode='wait'>
        {hasPrerequisites && selectedDateTime && !error && (
          <motion.div
            key='selected-slot-confirmation'
            initial={{
              opacity: 0,
              height: 0,
              y: -10,
              scale: 0.95,
            }}
            animate={{
              opacity: 1,
              height: 'auto',
              y: 0,
              scale: 1,
              transition: {
                opacity: { duration: 0.2, ease: 'easeOut' },
                height: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
                y: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
                scale: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
              },
            }}
            exit={{
              opacity: 0,
              height: 0,
              y: -10,
              scale: 0.95,
              transition: {
                opacity: { duration: 0.15, ease: 'easeIn' },
                height: { duration: 0.3, ease: [0.4, 0, 1, 1] },
                y: { duration: 0.3, ease: [0.4, 0, 1, 1] },
                scale: { duration: 0.3, ease: [0.4, 0, 1, 1] },
              },
            }}
            style={{ overflow: 'hidden' }}
          >
            <Card className='border-2 border-success bg-success-50'>
              <CardBody>
                <div className='flex items-center justify-between gap-3'>
                  <div className='flex items-center gap-3'>
                    {scheduleSettings.showInspectorInOnlineScheduler &&
                      getSelectedInspector() && (
                        <Avatar
                          src={getSelectedInspector()?.thumb}
                          alt={`${getSelectedInspector()?.firstName} ${getSelectedInspector()?.lastName
                            }`}
                          size='md'
                        />
                      )}
                    <div className='flex-1'>
                      <div className='flex items-center gap-2'>
                        <FaCheckCircle className='text-success' size={20} />
                        <p className='font-semibold text-success-800'>
                          Appointment Scheduled
                        </p>
                      </div>
                      <p className='text-sm text-success-700'>
                        {tzDayjs(selectedDateTime).format('dddd, MMMM D, YYYY')}
                      </p>
                      <p className='text-sm text-success-700'>
                        {(() => {
                          const timeRange = getTimeRange(selectedDateTime);
                          return timeRange
                            ? `${timeRange.start} - ${timeRange.end}`
                            : tzDayjs(selectedDateTime).format('h:mma');
                        })()}
                        {scheduleSettings.showInspectorInOnlineScheduler &&
                          getSelectedInspector() && (
                            <>
                              {' • with '}
                              {getSelectedInspector()?.firstName}{' '}
                              {getSelectedInspector()?.lastName}
                            </>
                          )}
                      </p>
                      {!countdown.isExpired && (
                        <p className='text-xs text-warning-700'>
                          Hold expires in {countdown.minutes}:
                          {countdown.seconds.toString().padStart(2, '0')}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button
                      isIconOnly
                      color='danger'
                      variant='flat'
                      size='sm'
                      aria-label='Clear selection'
                      className='shrink-0'
                      onPress={handleClearSelection}
                    >
                      <FaTimes />
                    </Button>
                    <Button
                      variant='solid'
                      size='sm'
                      endContent={<FaArrowRight />}
                      className={`shrink-0 ${companyStyles.secondaryButtonClassName}`}
                      style={companyStyles.secondaryButtonStyles}
                      onPress={handleContinue}
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Calendar View - Only show if prerequisites are met and no error */}
      {hasPrerequisites && !error && (
        <>
          <CalendarView
            /* Public online scheduler: the Online Booking Cutoff Time applies here. */
            scheduledLocation="online"
            currentOptSlots={currentOptSlots}
            isLoading={slotsLoading}
            isBackgroundLoading={isBackgroundLoading}
            selectedDateTime={selectedDateTime}
            onTimeSlotSelect={handleTimeSlotSelect}
            startDate={startDate}
            onWeekChange={handleWeekChange}
            selectedInspectorIds={selectedInspectorIdsForFilter}
            topRecommendations={topRecommendations}
            autoSelectTopInspector={true}
            inspectorFilter={
              <InspectorFilter
                selectedInspectorIds={selectedInspectorIdsForFilter}
                onSelectionChange={handleInspectorFilterChange}
                availableInspectorIds={availableInspectorIds}
              />
            }
          />

          {/* Continue Button */}
          <div className='flex justify-center pt-6'>
            <Button
              size='lg'
              endContent={<FaArrowRight />}
              onPress={handleContinue}
              isDisabled={!selectedDateTime}
              className={`min-w-48 font-semibold ${companyStyles.secondaryButtonClassName}`}
              style={companyStyles.secondaryButtonStyles}
            >
              Continue
            </Button>
          </div>

          {!selectedDateTime && (
            <p className='text-center text-sm text-gray-500'>
              Please select a time slot to continue
            </p>
          )}

          {/* Start Over Button */}
          <div className='flex justify-center pt-4'>
            <Button
              onPress={handleStartOver}
              variant='flat'
              startContent={<FaRotateLeft />}
              color='warning'
            >
              Start Over
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
